
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Heart, MessageCircle, Handshake, Users } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Post as PostType } from "@/lib/types";

import { ReportDialog } from "./ReportDialog";
import { PostDialog } from "./posts/PostDialog";
import { CommentForm } from "./CommentForm";
import { ShareButtons } from "./posts/ShareButtons";
import { Badge } from "./ui/badge";

interface PostProps {
  username: string;
  userId: string;
  content: string;
  imageUrl?: string;
  videoUrl?: string;
  likes: number;
  comments: { id: string; username: string; userId: string; content: string; createdAt: string }[];
  category: string;
  isAnonymous: boolean;
  createdAt: string;
  id: string;
}

export const Post = ({
  username,
  userId,
  content,
  imageUrl,
  videoUrl,
  likes,
  comments,
  category,
  isAnonymous,
  createdAt,
  id,
}: PostProps) => {
  const { toast } = useToast();
  const [localLikes, setLocalLikes] = useState(likes);
  const [hasLiked, setHasLiked] = useState(false);
  const [hasSupported, setHasSupported] = useState(false);
  const [supportCount, setSupportCount] = useState(0);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [localComments, setLocalComments] = useState(comments);
  const [groupInfo, setGroupInfo] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    const checkInitialStatus = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Check if user has liked this post
      const { data: reactions } = await supabase
        .from('post_reactions')
        .select('*')
        .eq('post_id', id)
        .eq('user_id', user.id)
        .eq('reaction_type', 'like');

      setHasLiked(reactions && reactions.length > 0);

      // Check if user has supported this post
      const { data: supports } = await supabase
        .from('post_reactions')
        .select('*')
        .eq('post_id', id)
        .eq('user_id', user.id)
        .eq('reaction_type', 'support');

      setHasSupported(supports && supports.length > 0);

      // Get total support count
      const { count } = await supabase
        .from('post_reactions')
        .select('*', { count: 'exact', head: true })
        .eq('post_id', id)
        .eq('reaction_type', 'support');

      setSupportCount(count || 0);

      // Check if this is a group post and fetch group info
      const { data: groupPost } = await supabase
        .from('group_posts')
        .select('group_id')
        .eq('post_id', id)
        .single();

      if (groupPost) {
        const { data: group } = await supabase
          .from('groups')
          .select('id, name')
          .eq('id', groupPost.group_id)
          .single();

        if (group) {
          setGroupInfo(group);
        }
      }
    };

    checkInitialStatus();
  }, [id]);

  const handleLike = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please sign in to like posts",
          variant: "destructive"
        });
        return;
      }

      if (!hasLiked) {
        // Optimistic update
        setLocalLikes(prev => prev + 1);
        setHasLiked(true);

        // Add reaction to database
        const { error: reactionError } = await supabase
          .from('post_reactions')
          .insert([{
            post_id: id,
            user_id: user.id,
            reaction_type: 'like'
          }]);

        if (reactionError) throw reactionError;

        toast({
          title: "Post liked! 💖",
          description: "Thanks for spreading the love!",
        });
      }
    } catch (error) {
      console.error('Error liking post:', error);
      // Revert optimistic update
      setLocalLikes(prev => prev - 1);
      setHasLiked(false);
      toast({
        title: "Error",
        description: "Could not like the post. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleSupport = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please sign in to support posts",
          variant: "destructive"
        });
        return;
      }

      if (!hasSupported) {
        // Optimistic update
        setSupportCount(prev => prev + 1);
        setHasSupported(true);

        const { error: reactionError } = await supabase
          .from('post_reactions')
          .insert([{
            post_id: id,
            user_id: user.id,
            reaction_type: 'support'
          }]);

        if (reactionError) throw reactionError;

        toast({
          title: "Support Given! 💜",
          description: "Your support means a lot to the community!",
        });
      }
    } catch (error) {
      console.error('Error supporting post:', error);
      // Revert optimistic update
      setSupportCount(prev => prev - 1);
      setHasSupported(false);
      toast({
        title: "Error",
        description: "Could not support the post. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleAddComment = async (content: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please sign in to comment",
          variant: "destructive"
        });
        return;
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('username')
        .eq('id', user.id)
        .single();

      if (!profile) throw new Error('Profile not found');

      const newComment = {
        post_id: id,
        user_id: user.id,
        content,
        created_at: new Date().toISOString()
      };

      const { data: commentData, error } = await supabase
        .from('comments')
        .insert([newComment])
        .select()
        .single();

      if (error) throw error;

      // Add the new comment to the local state
      setLocalComments([...localComments, {
        id: commentData.id,
        username: profile.username,
        userId: user.id,
        content: content,
        createdAt: commentData.created_at
      }]);

      toast({
        title: "Comment added! 💭",
        description: "Thanks for engaging in the conversation!",
      });

      setShowCommentForm(false);
    } catch (error) {
      console.error('Error adding comment:', error);
      toast({
        title: "Error",
        description: "Could not add comment. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Get background gradient based on category
  const getCategoryGradient = () => {
    switch (category) {
      case 'gratitude':
        return 'bg-gradient-to-br from-orange-100 to-yellow-50 dark:from-orange-900/30 dark:to-yellow-900/30';
      case 'support':
        return 'bg-gradient-to-br from-pink-100 to-red-50 dark:from-pink-900/30 dark:to-red-900/30';
      case 'encouragement':
        return 'bg-gradient-to-br from-green-100 to-teal-50 dark:from-green-900/30 dark:to-teal-900/30';
      default:
        return 'bg-gradient-to-br from-blue-100 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30';
    }
  };

  return (
    <>
      <div className={`rounded-3xl p-5 mb-4 animate-fade-in hover:shadow-lg cursor-pointer transition-all border-0 ${getCategoryGradient()}`} onClick={() => setIsDialogOpen(true)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isAnonymous ? (
              <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center shadow-md">
                <Users className="w-6 h-6 text-gray-500 dark:text-gray-400" />
              </div>
            ) : (
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-200 to-purple-200 flex items-center justify-center shadow-md border-2 border-white">
                <span className="text-lg font-semibold text-pink-600">{username.charAt(0).toUpperCase()}</span>
              </div>
            )}
            <div>
              <div className="font-semibold text-lg">{isAnonymous ? "Anonymous" : username}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">{new Date(createdAt).toLocaleString()}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {groupInfo && (
              <Badge variant="outline" className="flex items-center gap-1 bg-white/70 backdrop-blur-sm shadow-sm">
                <Users className="w-3 h-3" />
                {groupInfo.name}
              </Badge>
            )}
            <ReportDialog
              contentType="post"
              contentId={id}
              reportedUserId={userId}
              onReport={(reason) => {
                toast({
                  title: "Report submitted",
                  description: "Thank you for helping keep our community safe.",
                });
              }}
            />
          </div>
        </div>
        
        <p className="mt-3 mb-4 text-base">{content}</p>
        
        {imageUrl && (
          <div className="mb-4 relative aspect-video rounded-2xl overflow-hidden shadow-md">
            <img src={imageUrl} alt="Post content" className="object-cover w-full h-full" />
          </div>
        )}
        
        {videoUrl && (
          <div className="mb-4 relative aspect-video rounded-2xl overflow-hidden shadow-md">
            <video 
              src={videoUrl} 
              controls 
              className="w-full h-full"
              preload="metadata"
            >
              <track kind="captions" />
            </video>
          </div>
        )}

        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={(e) => {
                  e.stopPropagation();
                  handleLike();
                }} 
                className={`flex items-center gap-2 hover:scale-105 transition-transform ${hasLiked ? 'text-pink-500' : ''}`}
                disabled={hasLiked}
              >
                <Heart className={`w-5 h-5 ${hasLiked ? 'fill-current' : ''}`} />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="flex items-center gap-2 hover:scale-105 transition-transform"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowCommentForm(!showCommentForm);
                }}
              >
                <MessageCircle className="w-5 h-5" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className={`flex items-center gap-2 hover:scale-105 transition-transform ${hasSupported ? 'text-purple-500' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSupport();
                }}
                disabled={hasSupported}
              >
                <Handshake className={`w-5 h-5 ${hasSupported ? 'fill-current' : ''}`} />
              </Button>
            </div>
            <ShareButtons postId={id} content={content} />
          </div>
        </div>

        {showCommentForm && (
          <div className="mt-4 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl p-3" onClick={(e) => e.stopPropagation()}>
            <CommentForm
              onSubmit={handleAddComment}
              placeholder="Write a comment..."
              buttonText="Comment"
              postId={id}
            />
          </div>
        )}
      </div>

      <PostDialog
        post={{
          id,
          user_id: userId,
          content,
          image_url: imageUrl,
          video_url: videoUrl,
          category,
          is_anonymous: isAnonymous,
          likes_count: localLikes,
          created_at: createdAt,
          profiles: {
            username,
            avatar_url: ""
          },
          comments: localComments.map(c => ({
            id: c.id,
            content: c.content,
            user_id: c.userId,
            created_at: c.createdAt,
            profiles: {
              username: c.username,
              avatar_url: ""
            }
          }))
        } as PostType}
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
      />
    </>
  );
};
